import mergeWith from 'lodash/mergeWith';
import type {
	ContextualFields,
	IssueToCreate,
} from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import type { JiraIssueCreateInput } from '@atlassian/jira-relay/src/__generated__/issueCreateMutation_useIssueCreateMutation.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { convertFieldToIssueCreateInput } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import type { IssueInsertionArgs } from '../../../controllers/issue-connection-updater/index.tsx';

const mapContextualFieldDataToInput = (
	payload: ContextualFields,
): Partial<JiraIssueCreateInput> => {
	let rank;
	if (payload.rankBeforeIssueId) {
		rank = {
			beforeIssueId: payload.rankBeforeIssueId,
		};
	} else if (payload.rankAfterIssueId) {
		rank = {
			afterIssueId: payload.rankAfterIssueId,
		};
	}

	return {
		rank,
	};
};

export const toIssueCreateInput = (
	{ project, issueType, summary }: IssueToCreate,
	contextualFields: ContextualFields,
	groupContextFieldInput?: GroupContextField,
	insertionArgs?: IssueInsertionArgs,
	isIssueRankingEnabled?: boolean,
): JiraIssueCreateInput | null => {
	if (!summary.trim().length) {
		return null;
	}

	const input = {
		projectId: project.id,
		issueTypeId: issueType.id,
		fields: {
			singleLineTextFields: [
				{
					fieldId: 'summary',
					text: summary,
				},
			],
			...(contextualFields.parentIssueAri !== undefined && {
				parentField: { issueId: contextualFields.parentIssueAri },
			}),
			...(groupContextFieldInput &&
				fg('jira_spreadsheet_issue_create_in_group_targeting_') &&
				convertFieldToIssueCreateInput(groupContextFieldInput)),
		},
		rank:
			insertionArgs && isIssueRankingEnabled && fg('jsc_list_reranking_gate_technical')
				? {
						beforeIssueId: insertionArgs.insertAfter ? insertionArgs.insertNodeId : undefined,
						afterIssueId: insertionArgs.insertAfter ? undefined : insertionArgs.insertNodeId,
					}
				: undefined,
	};

	return mergeWith(input, mapContextualFieldDataToInput(contextualFields), (objValue, srcValue) => {
		if (Array.isArray(objValue)) {
			return objValue.concat(srcValue);
		}
	});
};
